import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Search,
  ChevronDown,
  X,
  ChevronsRight,
  ChevronsLeft,
  SlidersHorizontal,
  ArrowUpAZ,
  ArrowDownZA,
  ArrowUp10,
  ArrowDown10,
  XCircleIcon,
  ArrowUpDown,
  FileDown,
  Layout,
  Download,
} from "lucide-react";
import axios from "axios";
import { toast } from "react-toastify";
import "react-calendar/dist/Calendar.css";
import "../../css/CandidateReports.css";
import debounce from "lodash/debounce";
import * as XLSX from "xlsx";
import Slider from "rc-slider";
import "rc-slider/assets/index.css";
import Loader from "../Loader";
import host from "../../global";
import PlatformCalendar from "./platformCalendar";
import "../../css/platformCalendar.css";
import { calendarOverrideStyles } from "./datePickerStyles";
import DropDownWithSearch from "../shared/DropDownWIthSearch";
import { useTheme } from "../../hooks/useTheme";
import { useSelector } from "react-redux";

const CandidateReports = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {theme} = useTheme();
  const portal = location.pathname.startsWith("/hr") ? "hr" : "org-admin";
  const accessToken = sessionStorage.getItem("accessToken");
  const isNavigatingToEvaluation = useRef(false);
  const [searchQuery, setSearchQuery] = useState(
    localStorage.getItem("candidateReportsSearchQuery") ||
      location.state?.search_query ||
      ""
  );
  const [selectedJobRole, setSelectedJobRole] = useState(
    localStorage.getItem("candidateReportsJobRole") ||
      location.state?.job_position ||
      "Job Role"
  );
  const [selectedStatus, setSelectedStatus] = useState(
    localStorage.getItem("candidateReportsStatus") ||
      location.state?.combined_status ||
      "Status"
  );
  const [dateRange, setDateRange] = useState(() => {
    const storedDateRange = localStorage.getItem("candidateReportsDateRange");
    let result;
    if (storedDateRange) {
      try {
        result = JSON.parse(storedDateRange).map((date) => {
          const parsedDate = date ? new Date(date) : null;
          return parsedDate && !isNaN(parsedDate.getTime()) ? parsedDate : null;
        });
        if (Array.isArray(result) && result.length === 2) {
          return result;
        }
      } catch (e) {
        console.error("Failed to parse date range from localStorage:", e);
      }
    }
    if (location.state?.dateRange && Array.isArray(location.state.dateRange)) {
      result = location.state.dateRange.map((date) => {
        const parsedDate = date ? new Date(date) : null;
        return parsedDate && !isNaN(parsedDate.getTime()) ? parsedDate : null;
      });
      if (result.length === 2) {
        return result;
      }
    }
    return [null, null];
  });
  const [scoreRange, setScoreRange] = useState(() => {
    const storedScoreRange = localStorage.getItem("candidateReportsScoreRange");
    return storedScoreRange ? JSON.parse(storedScoreRange) : [0, 100];
  });
  const [tempSearchQuery, setTempSearchQuery] = useState(searchQuery);
  const [tempJobRole, setTempJobRole] = useState(selectedJobRole);
  const [tempStatus, setTempStatus] = useState(selectedStatus);
  const [tempDateRange, setTempDateRange] = useState(dateRange);
  const [tempScoreRange, setTempScoreRange] = useState(scoreRange);
  const [jobPositions, setJobPositions] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [selectedOwner, setSelectedOwner] = useState(
    localStorage.getItem("candidateReportsOwner") ||
      location.state?.owner ||
      "All"
  );
  const [tempOwner, setTempOwner] = useState(selectedOwner);
  const [isOwnerOpen, setIsOwnerOpen] = useState(false);
  const [isJobRoleOpen, setIsJobRoleOpen] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isColumnFilterOpen, setIsColumnFilterOpen] = useState(false);
  const [isStatusOpen, setIsStatusOpen] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [totalCandidates, setTotalCandidates] = useState(0);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [itemsPerPage] = useState(10);
  const [areCandidatesLoading, setAreCandidatesLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(() => {
    const storedPage = localStorage.getItem("candidateReportsCurrentPage");
    return storedPage
      ? Number(storedPage)
      : location.state?.page
      ? Number(location.state.page)
      : 1;
  });
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState(null);
  const [isSortOpen, setIsSortOpen] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const storedColumns = localStorage.getItem(
      "candidateReportsVisibleColumns"
    );
    return storedColumns
      ? JSON.parse(storedColumns)
      : {
          candidateName: true,
          jobRole: true,
          invitedDate: false,
          scheduledBy: false,
          interviewScore: true,
          status: true,
          contactNumber: true,
          email: true,
        };
  });
  const totalPages = Math.ceil(totalCandidates / itemsPerPage);
  const topRef = useRef(null);
  const filterRef = useRef(null);
  const columnFilterRef = useRef(null);
  const jobRoleDropdownRef = useRef(null);
  const statusDropdownRef = useRef(null);
  const ownerDropdownRef = useRef(null);
  const jobRoleListRef = useRef(null);
  const statusListRef = useRef(null);
  const nameSortDropdownRef = useRef(null);
  const scoreSortDropdownRef = useRef(null);
  const calendarRef = useRef(null);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [selectedJobRoleIndex, setSelectedJobRoleIndex] = useState(-1);
  const [selectedStatusIndex, setSelectedStatusIndex] = useState(-1);
  const statusOptions = ["Completed", "In Progress", "Pending"];
  const maxPageButtons = 5;
  const [isJobRoleSearching, setIsJobRoleSearching] = useState(false);
  const [jobRoleSearchQuery, setJobRoleSearchQuery] = useState("");
  const [filteredJobRoles, setFilteredJobRoles] = useState([]);
  const org_id = useSelector((state)=>state.userSlice.org_id);
  const handleSelectAllColumns = () => {
    const allColumns = {
      candidateName: true,
      jobRole: true,
      invitedDate: true,
      scheduledBy: true,
      interviewScore: true,
      status: true,
      contactNumber: true,
      email: true,
    };
    setVisibleColumns(allColumns);
    localStorage.setItem(
      "candidateReportsVisibleColumns",
      JSON.stringify(allColumns)
    );
  };

  const handleUnselectAllColumns = () => {
    const noColumns = {
      candidateName: false,
      jobRole: false,
      invitedDate: false,
      scheduledBy: false,
      interviewScore: false,
      status: false,
      contactNumber: false,
    };
    setVisibleColumns(noColumns);
    localStorage.setItem(
      "candidateReportsVisibleColumns",
      JSON.stringify(noColumns)
    );
  };

  const nonFixedColumnReset = () => {
    const resetColumns = {
      candidateName: true,
      jobRole: true,
      invitedDate: false,
      scheduledBy: false,
      interviewScore: true,
      status: true,
      contactNumber: true,
      email: true,
    };
    setVisibleColumns(resetColumns);
    localStorage.setItem(
      "candidateReportsVisibleColumns",
      JSON.stringify(resetColumns)
    );
    setIsColumnFilterOpen(false);
  };

  const debouncedFetchData = useRef(
    debounce((params, ownerFilter) => fetchData(params, ownerFilter), 500)
  ).current;

  const handleDownloadExcel = () => {
    const excelData = sortedCandidates.map((candidate) => {
      const row = {};
      if (visibleColumns.candidateName)
        row["Candidate Name"] = candidate?.candidate_name || "N/A";
      if (visibleColumns.jobRole)
        row["Job Role"] = candidate?.jdposition || "N/A";
      if (visibleColumns.email)
        row["Email"] = candidate?.candidate_email || "N/A";
      if (visibleColumns.invitedDate)
        row["Invited Date"] = candidate?.invited_date || "N/A";
      if (visibleColumns.scheduledBy)
        row["Owner"] = candidate?.scheduled_by_name || "N/A";
      if (visibleColumns.interviewScore)
        row["Interview Score"] =
          isNaN(candidate.interview_percent) ||
          candidate.interview_percent === "NA"
            ? "N/A"
            : Number(candidate.interview_percent).toFixed(2);
      if (visibleColumns.status)
        row["Status"] = candidate?.combined_status || "N/A";
      return row;
    });

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Candidates");

    const widths = [];
    if (visibleColumns.candidateName) widths.push({ wch: 20 });
    if (visibleColumns.jobRole) widths.push({ wch: 20 });
    if (visibleColumns.invitedDate) widths.push({ wch: 15 });
    if (visibleColumns.scheduledBy) widths.push({ wch: 20 });
    if (visibleColumns.interviewScore) widths.push({ wch: 15 });
    if (visibleColumns.status) widths.push({ wch: 12 });
    if (visibleColumns.email) widths.push({ wch: 30 });
    worksheet["!cols"] = widths;

    XLSX.writeFile(
      workbook,
      `Candidate_Reports_${new Date().toISOString().split("T")[0]}.xlsx`
    );
  };

  useEffect(() => {
    return () => {
      if (!isNavigatingToEvaluation.current) {
        localStorage.removeItem("candidateReportsSearchQuery");
        localStorage.removeItem("candidateReportsJobRole");
        localStorage.removeItem("candidateReportsStatus");
        localStorage.removeItem("candidateReportsOwner");
        localStorage.removeItem("candidateReportsDateRange");
        localStorage.removeItem("candidateReportsScoreRange");
        localStorage.removeItem("candidateReportsCurrentPage");
        localStorage.removeItem("candidateReportsVisibleColumns");
        localStorage.removeItem("whiteLabelStatus")
        sessionStorage.clear();
      }
    };
  }, []);

  const formatDate = (date, isEndDate = false) => {
    if (!date || isNaN(new Date(date).getTime())) return null;
    const formattedDate = new Date(date);
    if (isEndDate) {
      formattedDate.setHours(23, 59, 59, 999);
    } else {
      formattedDate.setHours(0, 0, 0, 0);
    }
    return formattedDate.toISOString();
  };
  const formatDateRange = (dateRange) => {
    if (!dateRange[0] || !dateRange[1]) return "Select Date Range";
    return `${dateRange[0].toLocaleDateString()} to ${dateRange[1].toLocaleDateString()}`;
  };


  const formatScoreRange = (scoreRange) => {
    return scoreRange[0] === 0 && scoreRange[1] === 100
      ? "All Scores"
      : `${scoreRange[0]} - ${scoreRange[1]}`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Completed":
        return "text-[#34B53A]";
      case "In Progress":
        return "text-[#FFB200]";
      case "Pending":
        return "text-[#DC0000]";
      default:
        return "text-[#D9D9D9]";
    }
  };

  const getScoreColor = (score) => {
    switch (true) {
      case score >= 60:
        return "text-[#1BAA0E]";
      case score >= 30 && score < 60:
        return "text-[#FFB82E]";
      case score >= 0 && score < 30:
        return "text-[#DC0000]";
      default:
        return "dark:text-[#CCCCCCE5] text-gray-500";
    }
  };

  const handleJobRoleSelect = (role) => {
    setTempJobRole(role);
    setIsJobRoleOpen(false);        // Close only the dropdown
    setSelectedJobRoleIndex(-1);
    setJobRoleSearchQuery("");     // Reset keyboard highlight
  };

  function handleSort(column) {
  if (sortColumn === column) {
    setSortDirection(sortDirection === "asc" ? "desc" : "asc");
  } else {
    setSortColumn(column);
    setSortDirection("desc"); // default high-to-low on new column
  }
  // Trigger or apply your sorting logic here
}


  const handleDateSelect = (value) => {
    setTempDateRange(value);
  };

  const handleStatusSelect = (status) => {
    setTempStatus(status);
    setIsStatusOpen(false);
  };

  const handleScoreRangeChange = (value) => {
    setTempScoreRange(value);
    console.log({tempScoreRange})
  };

  const handleSortClick = (column, direction) => {
    setSortColumn(column);
    setSortDirection(direction);
    setIsSortOpen(false);
  };

  const handleResetFilter = () => {
    setSelectedJobRole("Job Role");
    setSelectedStatus("Status");
    setSelectedOwner("All");
    setDateRange([null, null]);
    setScoreRange([0, 100]);
    setSearchQuery("");
    setCurrentPage(1);
    setSortColumn(null);
    setSortDirection(null);
    setVisibleColumns({
      candidateName: true,
      jobRole: true,
      invitedDate: false,
      scheduledBy: false,
      interviewScore: true,
      status: true,
      contactNumber: true,
      email:true,
    });
    setTempJobRole("Job Role");
    setTempStatus("Status");
    setTempOwner("All");
    setTempDateRange([null, null]);
    setTempScoreRange([0, 100]);
    setTempSearchQuery("");
    setIsFilterOpen(false);
    setIsCalendarOpen(false);
    localStorage.removeItem("candidateReportsSearchQuery");
    localStorage.removeItem("candidateReportsJobRole");
    localStorage.removeItem("candidateReportsStatus");
    localStorage.removeItem("candidateReportsOwner");
    localStorage.removeItem("candidateReportsDateRange");
    localStorage.removeItem("candidateReportsScoreRange");
    localStorage.removeItem("candidateReportsCurrentPage");
    localStorage.removeItem("candidateReportsVisibleColumns");
    const basePath = portal === "hr" ? "/hr" : "/org-admin";
    navigate(`${basePath}/candidatereports`, { replace: true });
    fetchData({
      page: 1,
      limit: itemsPerPage,
      job_position: "All",
      combined_status: "All",
      start_date: null,
      end_date: null,
      search_query: null,
      min_score: 0,
      max_score: 100,
    }, "All");
  };

  const handleResetDateRange = () => {
    setTempDateRange([null, null]);
  };

  const handleResetJobRole = () => {
    setTempJobRole("Job Role");
  };

  const handleResetStatus = () => {
    setTempStatus("Status");
  };

  const handleOwnerSelect = (owner) => {
    setTempOwner(owner);
    setIsOwnerOpen(false);
  };

  const handleResetOwner = () => {
    setTempOwner("All");
  };

  const handleResetScoreRange = () => {
    setTempScoreRange([0, 100]);
  };

  const handleResetKeywordSearch = () => {
    setTempSearchQuery("");
  };

  const handleColumnToggle = (column) => {
    setVisibleColumns((prev) => {
      const newColumns = { ...prev, [column]: !prev[column] };
      localStorage.setItem(
        "candidateReportsVisibleColumns",
        JSON.stringify(newColumns)
      );
      return newColumns;
    });
  };
  console.log({visibleColumns})
  const handleError = (error) => {
    if (error.response) {
      const { status, data } = error.response;
      if (status === 401) {
        sessionStorage.removeItem("accessToken");
        toast.error(`Unauthorized: ${data.detail}`);
        navigate("/");
      } else if (status === 404) {
        toast.warning(`Not Found: ${data.detail}`);
      } else {
        toast.error(`Error: ${data.detail}`);
      }
    } else {
      toast.error("An unexpected error occurred. Please try again.");
      if (error.message === "UserUnAuthenticatedException") {
        navigate("/");
      }
    }
  };

  useEffect(() => {
    if (!isJobRoleOpen) {
      setFilteredJobRoles([]); // Reset when closed
      setJobRoleSearchQuery("");
      setIsJobRoleSearching(false);
      return;
    }

    if (jobRoleSearchQuery.trim() === "") {
      // Show all + "All Job Roles" when no search
      setFilteredJobRoles(jobPositions);
      setIsJobRoleSearching(false);
    } else {
      // Filter job roles
      const filtered = jobPositions.filter((role) =>
        role.toLowerCase().includes(jobRoleSearchQuery.toLowerCase())
      );
      setFilteredJobRoles(filtered);
      setIsJobRoleSearching(true);
    }
  }, [jobRoleSearchQuery, isJobRoleOpen, jobPositions]);

  useEffect(() => {
    localStorage.setItem(
      "candidateReportsDateRange",
      JSON.stringify(
        dateRange.map((date) =>
          date && !isNaN(date.getTime()) ? date.toISOString() : null
        )
      )
    );
  }, [dateRange]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        filterRef.current &&
        !filterRef.current.contains(event.target) &&
        !calendarRef.current?.contains(event.target)
      ) {
        setIsFilterOpen(false);
        setIsCalendarOpen(false);
      }
      if (
        columnFilterRef.current &&
        !columnFilterRef.current.contains(event.target)
      ) {
        setIsColumnFilterOpen(false);
      }
      if (
        jobRoleDropdownRef.current &&
        !jobRoleDropdownRef.current.contains(event.target)
      ) {
        setIsJobRoleOpen(false);
        setJobRoleSearchQuery("");
        setSelectedJobRoleIndex(-1);
      }
      if (
        statusDropdownRef.current &&
        !statusDropdownRef.current.contains(event.target)
      ) {
        setIsStatusOpen(false);
      }
      if (
        ownerDropdownRef.current &&
        !ownerDropdownRef.current.contains(event.target)
      ) {
        setIsOwnerOpen(false);
      }
      
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const fetchJobPositions = async () => {
      try {
        const response = await axios.get(`${host}/jobroles`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        setJobPositions(response.data || []);
      } catch (error) {
        handleError(error);
      }
    };
    fetchJobPositions();
  }, [accessToken]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const allUsers = [];
        const hrNames = new Set();
        
        // Try to get HR profiles - backend should handle HR vs Admin access internally
        try {
          const hrEndpoints = [
            `${host}/orgadmin/get_hrprofiles`, // Should work for both HR and Admin
            `${host}/gethrdetails`,
            `${host}/hr/get_hrprofiles`,
            `${host}/get_all_users`
          ];
          
          let hrResponse = null;
          for (const endpoint of hrEndpoints) {
            try {
              hrResponse = await axios.get(endpoint, {
                headers: { Authorization: `Bearer ${accessToken}` },
              });
              break;
            } catch (err) {
              continue;
            }
          }
          
          if (!hrResponse) {
            throw new Error("All HR endpoints failed");
          }
          
          // Handle different possible response structures
          let hrData = hrResponse.data;
          if (hrData && !Array.isArray(hrData)) {
            // If it's an object, try to find the array inside
            hrData = hrData.hr_profiles || hrData.users || hrData.data || hrData.hrdetails || [];
          }
          
          const hrUsers = (hrData || []).map(user => {
            return {
              name: user.hrName || user.name || user.username || user.full_name,
              type: 'HR',
              id: user.hrId || user.id || user.userId,
              email: user.hremail || user.email
            };
          }).filter(user => user.name);
          
          // Keep track of HR names to identify admins later
          hrUsers.forEach(user => hrNames.add(user.name));
          allUsers.push(...hrUsers);
        } catch (error) {
          console.error("Error fetching HR profiles:", error);
        }
        
        // Extract ALL unique owners from candidates (includes both HR and Admin names)
        const candidateOwners = [...new Set(
          candidates
            .map(candidate => candidate.scheduled_by_name)
            .filter(name => name && name !== "N/A" && name.trim() !== "")
        )].sort();
        
        
        // If HR API failed or returned no data, treat all candidate owners as potential users
        if (hrNames.size === 0) {
          candidateOwners.forEach(ownerName => {
            allUsers.push({
              name: ownerName,
              type: 'HR/Admin', // Can't determine exact type without API
              id: null,
              email: null
            });
          });
        } else {
          // Add owners that are NOT in HR list (these are likely admins)
          candidateOwners.forEach(ownerName => {
            if (!hrNames.has(ownerName)) {
              // This owner is not in HR list, likely an admin
              allUsers.push({
                name: ownerName,
                type: 'Admin',
                id: null,
                email: null
              });
            }
          });
        }
        
        // Also add any missing HR names from candidates that might not be in API
        candidateOwners.forEach(ownerName => {
          if (hrNames.has(ownerName)) {
            // Make sure HR user is in the list (in case API failed but candidate data has it)
            const existingUser = allUsers.find(user => user.name === ownerName);
            if (!existingUser) {
              allUsers.push({
                name: ownerName,
                type: 'HR',
                id: null,
                email: null
              });
            }
          }
        });
        
        // Remove duplicates and sort
        const uniqueUsers = allUsers.filter((user, index, self) => 
          index === self.findIndex(u => u.name === user.name)
        ).sort((a, b) => {
          // Sort Admins first, then HRs
          if (a.type === 'Admin' && b.type !== 'Admin') return -1;
          if (a.type !== 'Admin' && b.type === 'Admin') return 1;
          return a.name.localeCompare(b.name);
        });
        setAllUsers(uniqueUsers);
        
      } catch (error) {
        console.error("Error fetching users:", error);
        // Ultimate fallback: just use candidate data
        const uniqueOwners = [...new Set(
          candidates
            .map(candidate => candidate.scheduled_by_name)
            .filter(name => name && name !== "N/A")
        )].sort().map(name => ({ name, type: 'Unknown' }));
        setAllUsers(uniqueOwners);
      }
    };
    fetchUsers();


  }, [accessToken, candidates]);

  const fetchData = async (params, ownerFilter = selectedOwner) => {
    try {
      setAreCandidatesLoading(true);
      
      // Add owner filter to API parameters for backend filtering
      const apiParams = { 
        ...params,
        scheduled_by_name: ownerFilter === "All" ? undefined : ownerFilter,
        org_id:org_id
      };
      
      const response = await axios.get(`${host}/get_candidates_data`, {
        params: apiParams,
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      
      console.log("API Response - Total candidates:", response.data.total_count);
      console.log("API Response - First few candidates scheduled_by_name:", response.data.candidates?.slice(0, 5).map(c => c.scheduled_by_name));
      
      setCandidates(response.data.candidates || []);
      setTotalCandidates(response.data.total_count || 0);
      setCurrentUserId(response.data.current_user_id || null);
      
      
    } catch (error) {
      handleError(error);
    } finally {
      setAreCandidatesLoading(false);
    }
  };

  useEffect(() => {
    const params = {
      page: currentPage,
      limit: itemsPerPage,
      job_position: selectedJobRole === "Job Role" ? "All" : selectedJobRole,
      combined_status: selectedStatus === "Status" ? "All" : selectedStatus,
      start_date: formatDate(dateRange[0]),
      end_date: formatDate(dateRange[1], true),
      search_query: searchQuery || null,
      min_score: scoreRange[0],
      max_score: scoreRange[1],
    };
    debouncedFetchData(params, selectedOwner);
  }, [
    currentPage,
    accessToken,
    searchQuery,
    selectedJobRole,
    selectedStatus,
    selectedOwner,
    dateRange,
    scoreRange,
  ]);

  const sortedCandidates = [...candidates].sort((a, b) => {
    if (sortColumn === "name") {
      const nameA = (a.candidate_name || "").toLowerCase();
      const nameB = (b.candidate_name || "").toLowerCase();
      return sortDirection === "asc"
        ? nameA.localeCompare(nameB)
        : nameB.localeCompare(nameA);
    } else if (
      sortColumn === "interview_score" &&
      visibleColumns.interviewScore
    ) {
      const scoreA =
        isNaN(a.interview_percent) || a.interview_percent === "NA"
          ? 0
          : Number(a.interview_percent);
      const scoreB =
        isNaN(b.interview_percent) || b.interview_percent === "NA"
          ? 0
          : Number(b.interview_percent);
      return sortDirection === "asc" ? scoreA - scoreB : scoreB - scoreA;
    }
    return 0;
  });

  const onViewCandidate = (interview_id) => {
    isNavigatingToEvaluation.current = true;
    const basePath = portal === "hr" ? "/hr" : "/org-admin";
    navigate(`${basePath}/evaluationpage/${interview_id}`, {
      state: {
        interview_id,
        page: currentPage,
        job_position: selectedJobRole,
        combined_status: selectedStatus,
        owner: selectedOwner,
        search_query: searchQuery,
        dateRange: dateRange.map((date) =>
          date && !isNaN(date.getTime()) ? date.toISOString() : null
        ),
        scoreRange,
      },
    });
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const getPageRange = () => {
    let startPage = currentPage;
    if (currentPage > totalPages - maxPageButtons + 1) {
      startPage = Math.max(1, totalPages - maxPageButtons + 1);
    }
    const endPage = Math.min(startPage + maxPageButtons - 1, totalPages);
    return Array.from(
      { length: endPage - startPage + 1 },
      (_, i) => startPage + i
    );
  };

  const activeFilters = [];
  if (tempJobRole !== "Job Role") {
    activeFilters.push({
      label: `Job Role: ${tempJobRole}`,
      onRemove: handleResetJobRole,
    });
  }
  if (tempDateRange[0] && tempDateRange[1]) {
    activeFilters.push({
      label: `Date: ${formatDateRange(tempDateRange)}`,
      onRemove: handleResetDateRange,
    });
  }
  if (tempStatus !== "Status") {
    activeFilters.push({
      label: `Status: ${tempStatus}`,
      onRemove: handleResetStatus,
    });
  }
  if (tempOwner !== "All") {
    activeFilters.push({
      label: `Owner: ${tempOwner}`,
      onRemove: handleResetOwner,
    });
  }
  if (tempScoreRange[0] !== 0 || tempScoreRange[1] !== 100) {
    activeFilters.push({
      label: `Score: ${formatScoreRange(tempScoreRange)}`,
      onRemove: handleResetScoreRange,
    });
  }
  const filterCount = activeFilters.length;



  return (
    <>
      <style>{calendarOverrideStyles}</style>
      <div
        className="min-h-screen border rounded-2xl dark:border-gray-700/50 dark:glass-effect bg-bgSecondary border-gray-400/50  transition-colors"
        ref={topRef}
      >
        <div className="px-12 pt-8">
          <div className="flex items-center justify-between mb-8">
            <h1 className="text-xl dark:text-[#D9D9D9] text-textPrimary font-normal">
              Candidates Report
            </h1>
            <div className="flex items-center space-x-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 dark:bg-transparent bg-bgPrimary" />
                <input
                  type="text"
                  placeholder="Search by name..."
                  value={tempSearchQuery}
                  onChange={(e) => {
                    setTempSearchQuery(e.target.value);
                    setSearchQuery(e.target.value);
                    setCurrentPage(1);
                    localStorage.setItem(
                      "candidateReportsSearchQuery",
                      e.target.value
                    );
                  }}
                  className={`w-64 ${theme === 'dark' ? "glass-effect":"bg-bgPrimary"}  border border-gray-600 rounded-full pl-10 pr-4 py-2 text-textPrimary dark:text-[#EDEDEDE5] placeholder-gray-500 focus:outline-none focus:border-gray-400 dark:focus:border-white text-sm`}
                />
                {tempSearchQuery && (
                  <XCircleIcon
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 dark:text-gray-400 text-textSecondary dark:hover:text-white hover:text-textPrimary cursor-pointer"
                    onClick={() => {
                      handleResetKeywordSearch();
                      setSearchQuery("");
                      setCurrentPage(1);
                      localStorage.removeItem("candidateReportsSearchQuery");
                    }}
                  />
                )}
              </div>
              <div className="relative" ref={filterRef}>
                <button
                  className={`flex items-center space-x-4 ${theme === 'dark' ? 'glass-effect ' : 'bg-bgPrimary'} border border-gray-600 rounded-full px-4 py-2 dark:text-gray-300 text-textSecondary dark:hover:bg-gray-700/50 hover:bg-gray-50 text-sm font-light`}
                  onClick={() => setIsFilterOpen(!isFilterOpen)}
                >
                  <SlidersHorizontal className="w-4 h-4 dark:text-white text-textSecondary" />
                  <span>Filter</span>
                  {filterCount > 0 && (
                    <>
                      <span className="ml-2 bg-gradient-to-r from-pink-500 to-blue-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs absolute -right-2 -top-1">
                        {filterCount}
                      </span>
                      <XCircleIcon
                        className="w-5 h-5"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleResetFilter();
                        }}
                      />
                    </>
                  )}
                </button>
                {isFilterOpen && (
                  <div
                    className="absolute right-0 mt-2 w-[400px] border border-gray-600 rounded-lg shadow-lg z-20 p-6 dark:bg-[linear-gradient(233.57deg,rgba(34,52,97,0.25)_-5.01%,rgba(11,10,31,0.05)_100%)] bg-bgPrimary dark:backdrop-blur-[500px] opacity-100"
                  >
                    <div
                      className={`flex items-center justify-between mb-4 ${
                        isJobRoleOpen || isStatusOpen || isOwnerOpen ? "" : ""
                      }`}
                    >
                      <h3 className="text-lg font-medium dark:text-[#D9D9D9] text-textPrimary">
                        Filter
                      </h3>
                      <button
                        onClick={() => setIsFilterOpen(false)}
                        className="dark:text-gray-400 dark:hover:text-white text-textPrimary hover:text-textSecondary"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                    {activeFilters.length > 0 && (
                      <div
                        className={`mb-4 ${
                          isJobRoleOpen || isStatusOpen || isOwnerOpen ? "" : ""
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <label className="text-sm font-medium dark:text-[#D8D8D8] text-textSecondary">
                            Active Filters
                          </label>
                          <button
                            onClick={handleResetFilter}
                            className="text-xs text-blue-400 hover:text-blue-300"
                          >
                            Clear All
                          </button>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {activeFilters.map((filter, index) => (
                            <div
                              key={index}
                              className="flex items-center dark:bg-gray-700/50 bg-violet-600 dark:text-[#D9D9D9] text-xs px-3 py-1 rounded-full dark:border-green-400 border border-violet-900 text-white"
                            >
                              <span>{filter.label}</span>
                              <button
                                onClick={filter.onRemove}
                                className="ml-2 dark:text-gray-400 hover:text-white text-gray-200"
                              >
                                <XCircleIcon className="w-4 h-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    <div
                      className={`mb-6 ${
                        isJobRoleOpen || isStatusOpen || isOwnerOpen ? "" : ""
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-sm font-medium dark:text-[#D8D8D8] text-textSecondary">
                          Date Range
                        </label>
                      </div>
                      <div className="relative" ref={calendarRef}>
                        <button
                          className="flex items-center justify-between w-full dark:glass-effect bg-bgPrimary border border-gray-600 rounded-full px-4 py-2 dark:text-gray-300 text-textPrimary dark:hover:bg-gray-700/50 hover:bg-gray-50 text-sm font-light h-10"
                          onClick={(e) => {
                            e.stopPropagation();
                            setIsCalendarOpen(!isCalendarOpen);
                          }}
                          aria-expanded={isCalendarOpen}
                        >
                          <span className="truncate">
                            {formatDateRange(tempDateRange)}
                          </span>
                          {tempDateRange?.length === 2 && tempDateRange[0] ? (
                            <X
                              className="w-4 h-4 ml-2"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleResetDateRange();
                                setIsCalendarOpen(false);
                              }}
                            />
                          ) : (
                            <ChevronDown className="w-4 h-4 ml-2" />
                          )}
                        </button>
                        {isCalendarOpen && (
                          <div
                            className="candidate-analytics-calendar absolute mt-2 z-[100] right-0"
                            style={{
                              background: theme === 'dark' ? "#0a1733" : "#fff",
                              border: "1px solid #4b5563",
                              borderRadius: "8px"
                            }}
                          >
                            <div className={`platform-calendar ${theme}`}>
                              <PlatformCalendar
                                onChange={(value) => {
                                  handleDateSelect(value);
                                  if (value[0] && value[1]) {
                                    setIsCalendarOpen(false);
                                  }
                                }}
                                value={tempDateRange}
                                selectRange={true}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="mb-6">
                      <div
                        className={`flex items-center justify-between mb-2 ${
                          isJobRoleOpen || isStatusOpen || isOwnerOpen ? "" : ""
                        }`}
                      >
                        <label className="text-sm font-medium dark:text-[#D8D8D8] text-textSecondary">
                          Job Role
                        </label>
                        <button
                          onClick={handleResetJobRole}
                          className="text-xs text-blue-400 hover:text-blue-300"
                        >
                          Reset
                        </button>
                      </div>
                      <div className="relative" ref={jobRoleDropdownRef}>
                        {/* <button
                          className="flex items-center justify-between w-full glass-effect border border-gray-600 rounded-full px-4 py-2 text-gray-300 hover:bg-gray-700/50 text-sm font-light"
                          onClick={() => {
                            setIsJobRoleOpen(!isJobRoleOpen);
                            if (!isJobRoleOpen) {
                              setSelectedJobRoleIndex(0);
                            }
                          }}
                          onKeyDown={(e) => {
                            const allRoles = ["Job Role", ...jobPositions];
                            
                            // Handle Arrow Keys to navigate dropdown
                            if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                              e.preventDefault();
                              if (!isJobRoleOpen) {
                                setIsJobRoleOpen(true);
                                setSelectedJobRoleIndex(e.key === 'ArrowDown' ? 0 : allRoles.length - 1);
                              } else {
                                // Navigate through options
                                setSelectedJobRoleIndex(prev => {
                                  const newIndex = e.key === 'ArrowDown' 
                                    ? (prev < allRoles.length - 1 ? prev + 1 : 0)
                                    : (prev > 0 ? prev - 1 : allRoles.length - 1);
                                  
                                  // Auto-scroll to highlighted item
                                  setTimeout(() => {
                                    const listElement = jobRoleListRef.current;
                                    const selectedElement = listElement?.children[newIndex];
                                    if (selectedElement && listElement) {
                                      selectedElement.scrollIntoView({
                                        behavior: 'smooth',
                                        block: 'nearest'
                                      });
                                    }
                                  }, 0);
                                  return newIndex;
                                });
                              }
                              return;
                            }
                            
                            // Handle Enter key
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              if (!isJobRoleOpen) {
                                setIsJobRoleOpen(true);
                                setSelectedJobRoleIndex(0);
                              } else if (selectedJobRoleIndex >= 0) {
                                const selectedRole = allRoles[selectedJobRoleIndex];
                                handleJobRoleSelect(selectedRole);
                                setSelectedJobRoleIndex(-1);
                              }
                              return;
                            }
                            
                            // Handle Escape key
                            if (e.key === 'Escape') {
                              setIsJobRoleOpen(false);
                              setSelectedJobRoleIndex(-1);
                              return;
                            }
                          }}
                          aria-expanded={isJobRoleOpen}
                          aria-haspopup="listbox"
                          aria-controls="job-role-listbox"
                          aria-label="Select job role filter"
                          aria-activedescendant={selectedJobRoleIndex >= 0 ? `job-role-option-${selectedJobRoleIndex}` : undefined}
                          tabIndex={0}
                        >
                          <span
                            style={{
                              maxWidth: "90%",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {tempJobRole}
                          </span>
                          <ChevronDown className="w-4 h-4 flex-shrink-0" />
                        </button>
                        {isJobRoleOpen && (
                          <div
                            className="absolute mt-2 w-full border border-gray-600 rounded-md shadow-lg z-30"
                            style={{
                              background: "#0a1733",
                              opacity: 1,
                              backdropFilter: "blur(500px)",
                              maxHeight: "300px",
                            }}
                          >
                            <ul 
                              ref={jobRoleListRef}
                              className="py-1 text-gray-300 max-h-[200px] overflow-y-auto text-sm"
                              id="job-role-listbox"
                              role="listbox"
                            >
                              <li
                                className={`px-4 py-2 cursor-pointer transition-colors ${
                                  selectedJobRoleIndex === 0 ? 'bg-gray-700 text-white' : 'hover:bg-gray-700'
                                }`}
                                onClick={() => {
                                  handleJobRoleSelect("Job Role");
                                  setSelectedJobRoleIndex(-1);
                                }}
                                onMouseEnter={() => setSelectedJobRoleIndex(0)}
                                role="option"
                                aria-selected={tempJobRole === "Job Role"}
                                id="job-role-option-0"
                              >
                                All
                              </li>
                              {jobPositions.map((role, index) => (
                                <li
                                  key={index}
                                  className={`px-4 py-2 cursor-pointer transition-colors ${
                                    selectedJobRoleIndex === index + 1 ? 'bg-gray-700 text-white' : 'hover:bg-gray-700'
                                  }`}
                                  onClick={() => {
                                    handleJobRoleSelect(role);
                                    setSelectedJobRoleIndex(-1);
                                  }}
                                  onMouseEnter={() => setSelectedJobRoleIndex(index + 1)}
                                  role="option"
                                  aria-selected={tempJobRole === role}
                                  id={`job-role-option-${index + 1}`}
                                >
                                  {role}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )} */}
                        <div className="relative" ref={jobRoleDropdownRef}>
  {/* Combined Button / Search Input */}
  {isJobRoleOpen ? (
    // Search Input Mode
    <input
      type="text"
      placeholder="Search Job Roles..."
      value={jobRoleSearchQuery}
      onChange={(e) => setJobRoleSearchQuery(e.target.value)}
      autoFocus
      className="w-full dark:glass-effect border border-gray-600 rounded-full px-4 py-2 text-sm font-light 
             bg-bgPrimary dark:bg-[#0a1733] 
             text-gray-800 dark:text-white 
             placeholder-gray-500 dark:placeholder-gray-400 
             focus:outline-none focus:border-gray-400"
      // className="w-full dark:glass-effect border border-gray-600 rounded-full px-4 py-2 text-sm font-light text-gray-300 placeholder-gray-500 focus:outline-none focus:border-gray-400"
      onKeyDown={(e) => {
        const allOptions = ["Job Role", ...filteredJobRoles];
        const totalItems = allOptions.length;

        if (e.key === "ArrowDown") {
          e.preventDefault();
          setSelectedJobRoleIndex((prev) =>
            prev < totalItems - 1 ? prev + 1 : 0
          );
        } else if (e.key === "ArrowUp") {
          e.preventDefault();
          setSelectedJobRoleIndex((prev) =>
            prev <= 0 ? totalItems - 1 : prev - 1
          );
        } else if (e.key === "Enter") {
          e.preventDefault();
          if (selectedJobRoleIndex >= 0) {
            const selected = allOptions[selectedJobRoleIndex];
            handleJobRoleSelect(selected);
          }
        } else if (e.key === "Escape") {
          setIsJobRoleOpen(false);
          setJobRoleSearchQuery("");
          setSelectedJobRoleIndex(-1);
        }
      }}
    />
  ) : (
    // Normal Button Mode
    <button
      className="flex items-center justify-between w-full dark:glass-effect border border-gray-600 rounded-full px-4 py-2 dark:text-gray-300 text-textSecondary dark:hover:bg-gray-700/50 hover:bg-gray-50 text-sm font-light"
      onClick={() => {
        setIsJobRoleOpen(true);
        setJobRoleSearchQuery("");
        setFilteredJobRoles(jobPositions);
        setSelectedJobRoleIndex(0);
      }}
    >
      <span
        style={{
          maxWidth: "90%",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {tempJobRole}
      </span>
      <ChevronDown
        className={`w-4 h-4 ml-2 transform transition-transform ${
          isJobRoleOpen ? "rotate-180" : ""
        }`}
      />
    </button>
  )}

  {/* Dropdown List - Only shown when open */}
  {isJobRoleOpen && (
    <div className="absolute mt-2 w-full border border-gray-600 rounded-md shadow-lg z-30 bg-bgPrimary dark:bg-[#0a1733] max-h-[300px] overflow-hidden">
      <ul
        ref={jobRoleListRef}
        className="py-1 max-h-[200px] overflow-y-auto text-xs dark:text-gray-300 text-gray-700"
        role="listbox"
        id="job-role-listbox"
      >
        {/* All Job Roles */}
        <li
          className={`px-4 py-3 cursor-pointer transition-colors ${
            selectedJobRoleIndex === 0
              ? "dark:bg-gray-700 bg-gray-300 dark:text-white"
              : "dark:hover:bg-gray-700 hover:bg-gray-50"
          }`}
          onClick={() => handleJobRoleSelect("Job Role")}
          onMouseEnter={() => setSelectedJobRoleIndex(0)}
          role="option"
          aria-selected={tempJobRole === "Job Role"}
        >
          <span className="text-sm">All Job Roles</span>
        </li>

        {/* Filtered Results */}
        {filteredJobRoles.length > 0 ? (
          filteredJobRoles.map((role, index) => (
            <li
              key={role}
              className={`px-4 py-3 cursor-pointer transition-colors ${
                selectedJobRoleIndex === index + 1
                  ? "dark:bg-gray-700 bg-gray-300 dark:text-white"
                  : "dark:hover:bg-gray-700 hover:bg-gray-50"
              }`}
              onClick={() => handleJobRoleSelect(role)}
              onMouseEnter={() => setSelectedJobRoleIndex(index + 1)}
              role="option"
              aria-selected={tempJobRole === role}
            >
              <span className="text-sm">{role}</span>
            </li>
          ))
        ) : jobRoleSearchQuery ? (
          <li className="px-4 py-3 text-gray-400 text-center text-sm">
            No job roles found
          </li>
        ) : jobPositions.length === 0 ? (
          <li className="px-4 py-3 text-gray-400 text-center text-sm">
            No job roles available
          </li>
        ) : null}
      </ul>
    </div>
  )}
</div>
                      </div>
                    </div>
                    <div className="mb-6">
                      <div
                        className={`flex items-center justify-between mb-2 ${
                          isJobRoleOpen || isStatusOpen || isOwnerOpen ? "" : ""
                        }`}
                      >
                        <label className="text-sm font-medium dark:text-[#D8D8D8] text-textSecondary">
                          Status
                        </label>
                        <button
                          onClick={handleResetStatus}
                          className="text-xs text-blue-400 hover:text-blue-300"
                        >
                          Reset
                        </button>
                      </div>
                      <div className="relative" ref={statusDropdownRef}>
                        <button
                          className="flex items-center justify-between w-full dark:glass-effect border border-gray-600 rounded-full px-4 py-2 dark:text-gray-300 text-textSecondary dark:hover:bg-gray-700/50 text-sm font-light hover:bg-gray-50"
                          onClick={() => {
                            setIsStatusOpen(!isStatusOpen);
                            if (!isStatusOpen) {
                              setSelectedStatusIndex(0);
                            }
                          }}
                          onKeyDown={(e) => {
                            const allStatuses = ["Status", ...statusOptions];
                            
                            // Handle Arrow Keys to navigate dropdown
                            if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                              e.preventDefault();
                              if (!isStatusOpen) {
                                setIsStatusOpen(true);
                                setSelectedStatusIndex(e.key === 'ArrowDown' ? 0 : allStatuses.length - 1);
                              } else {
                                // Navigate through options
                                setSelectedStatusIndex(prev => {
                                  const newIndex = e.key === 'ArrowDown' 
                                    ? (prev < allStatuses.length - 1 ? prev + 1 : 0)
                                    : (prev > 0 ? prev - 1 : allStatuses.length - 1);
                                  
                                  // Auto-scroll to highlighted item
                                  setTimeout(() => {
                                    const listElement = statusListRef.current;
                                    const selectedElement = listElement?.children[newIndex];
                                    if (selectedElement && listElement) {
                                      selectedElement.scrollIntoView({
                                        behavior: 'smooth',
                                        block: 'nearest'
                                      });
                                    }
                                  }, 0);
                                  return newIndex;
                                });
                              }
                              return;
                            }
                            
                            // Handle Enter key
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              if (!isStatusOpen) {
                                setIsStatusOpen(true);
                                setSelectedStatusIndex(0);
                              } else if (selectedStatusIndex >= 0) {
                                const selectedStatus = allStatuses[selectedStatusIndex];
                                handleStatusSelect(selectedStatus);
                                setSelectedStatusIndex(-1);
                              }
                              return;
                            }
                            
                            // Handle Escape key
                            if (e.key === 'Escape') {
                              setIsStatusOpen(false);
                              setSelectedStatusIndex(-1);
                              return;
                            }
                          }}
                          aria-expanded={isStatusOpen}
                          aria-haspopup="listbox"
                          aria-controls="status-listbox"
                          aria-label="Select status filter"
                          aria-activedescendant={selectedStatusIndex >= 0 ? `status-option-${selectedStatusIndex}` : undefined}
                          tabIndex={0}
                        >
                          <span
                            style={{
                              maxWidth: "90%",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {tempStatus}
                          </span>
                          <ChevronDown className="w-4 h-4 flex-shrink-0" />
                        </button>
                        {isStatusOpen && (
                          <div
                            className="absolute mt-2 w-full border border-gray-600 rounded-md shadow-lg z-30  bg-bgPrimary opacity-100   !backdrop-blur-0 max-h-[300px] dark:bg-[#0a1733]"    
                          >
                            <ul 
                              ref={statusListRef}
                              className="py-1 dark:text-gray-300 text-gray-700 max-h-[200px] overflow-y-auto text-xs"
                              id="status-listbox"
                              role="listbox"
                            >
                              <li
                                className={`px-4 py-2 cursor-pointer transition-colors ${
                                  selectedStatusIndex === 0 ? 'dark:bg-gray-700 bg-gray-300 dark:text-white text-textSecondary' : 'dark:hover:!bg-gray-700 hover:!bg-gray-50'
                                }`}
                                onClick={() => {
                                  handleStatusSelect("Status");
                                  setSelectedStatusIndex(-1);
                                }}
                                onMouseEnter={() => setSelectedStatusIndex(0)}
                                role="option"
                                aria-selected={tempStatus === "Status"}
                                id="status-option-0"
                              >
                                All Status
                              </li>
                              {statusOptions.map((status, index) => (
                                <li
                                  key={index}
                                  className={`px-4 py-2 cursor-pointer transition-colors ${
                                    selectedStatusIndex === index + 1 ? 'dark:bg-gray-700 bg-gray-300 dark:text-white text-textSecondary' : 'dark:hover:!bg-gray-700 hover:!bg-gray-50'
                                  }`}
                                  onClick={() => {
                                    handleStatusSelect(status);
                                    setSelectedStatusIndex(-1);
                                  }}
                                  onMouseEnter={() => setSelectedStatusIndex(index + 1)}
                                  role="option"
                                  aria-selected={tempStatus === status}
                                  id={`status-option-${index + 1}`}
                                >
                                  {status}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Owner Filter */}
                    <div className="mb-6">
                      <div
                        className={`flex items-center justify-between mb-2 ${
                          isJobRoleOpen || isStatusOpen || isOwnerOpen ? "" : ""
                        }`}
                      >
                        <label className="text-sm font-medium dark:text-[#D8D8D8] text-textSecondary">
                          Owner
                        </label>
                        <button
                          onClick={handleResetOwner}
                          className="text-xs text-blue-400 hover:text-blue-300"
                        >
                          Reset
                        </button>
                      </div>
                      <div className="relative" ref={ownerDropdownRef}>
                        <button
                          className="flex items-center justify-between w-full dark:glass-effect border border-gray-600 rounded-full px-4 py-2 dark:text-gray-300 text-textSecondary dark:hover:bg-gray-700/50 text-sm font-light hover:bg-gray-50"
                          onClick={() => setIsOwnerOpen(!isOwnerOpen)}
                          aria-expanded={isOwnerOpen}
                          aria-haspopup="listbox"
                          aria-controls="owner-listbox"
                          aria-label="Select owner filter"
                          tabIndex={0}
                        >
                          <span>{tempOwner}</span>
                          <ChevronDown
                            className={`w-4 h-4 ml-2 transform transition-transform ${
                              isOwnerOpen ? "rotate-180" : ""
                            }`}
                          />
                        </button>
                        {isOwnerOpen && (
                             <div
                                className="absolute mt-2 w-full border border-gray-600 rounded-md shadow-lg z-30  bg-bgPrimary opacity-100 dark:backdrop-blur-[500px] backdrop-blur-0 max-h-[300px] dark:bg-[#0a1733]"    
                              >
                              <ul 
                                className="py-1 dark:text-gray-300 text-gray-700 max-h-[200px] overflow-y-auto text-xs"
                                id="owner-listbox"
                                role="listbox"
                              >
                              <li
                                className="px-4 py-2 cursor-pointer transition-colors dark:hover:bg-gray-700 hover:bg-gray-50"
                                onClick={() => handleOwnerSelect("All")}
                                role="option"
                                aria-selected={tempOwner === "All"}
                              >
                                All Owners
                              </li>
                              {allUsers.length === 0 && (
                                <li className="px-4 py-2 text-gray-500 text-xs">
                                  No users found
                                </li>
                              )}
                              {allUsers.map((user, index) => {
                                const userName = user.name;
                                if (!userName) return null;
                                
                                return (
                                  <li
                                    key={index}
                                      className="px-4 py-2 cursor-pointer transition-colors dark:hover:bg-gray-700 hover:bg-gray-50"
                                    onClick={() => handleOwnerSelect(userName)}
                                    role="option"
                                    aria-selected={tempOwner === userName}
                                  >
                                    <div className="flex justify-between items-center">
                                      <span>{userName}</span>
                                      <span className="text-xs text-gray-500 ml-2">
                                        {user.type || 'HR'}
                                      </span>
                                    </div>
                                  </li>
                                );
                              })}
                              </ul>
                            </div>
                        )}
                      </div>
                    </div>

                    <div
                      className={`mb-6 ${
                        isJobRoleOpen || isStatusOpen || isOwnerOpen ? "" : ""
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-sm font-medium dark:text-[#D8D8D8] text-textSecondary">
                          Score Range
                        </label>
                        <button
                          onClick={handleResetScoreRange}
                          className="text-xs text-blue-400 hover:text-blue-300"
                        >
                          Reset
                        </button>
                      </div>
                      <div className="px-2">
                        <Slider
                          range
                          min={0}
                          max={100}
                          value={tempScoreRange}
                          onChange={handleScoreRangeChange}
                          trackStyle={[{ backgroundColor: "#2960FD" }]}
                          handleStyle={[
                            {
                              borderColor: "#2960FD",
                              backgroundColor: "#FFFFFF",
                            },
                            {
                              borderColor: "#2960FD",
                              backgroundColor: "#FFFFFF",
                            },
                          ]}
                          railStyle={{ backgroundColor: "#5A4479" }}
                        />
                        <div className="flex justify-between mt-2 text-sm dark:text-[#D8D8D8] text-textSecondary">
                          <span>{tempScoreRange[0]}</span>
                          <span>{tempScoreRange[1]}</span>
                        </div>
                      </div>
                    </div>
                    <div
                      className={`flex space-x-3 ${
                        isJobRoleOpen || isStatusOpen || isOwnerOpen ? "" : ""
                      }`}
                    >
                      <button
                        onClick={handleResetFilter}
                        className="flex-1 px-4 py-2 border border-gray-600 rounded-full dark:text-gray-300 text-textSecondary dark:hover:bg-gray-700/50 hover:bg-gray-200 text-sm"
                      >
                        Reset All
                      </button>
                      <button
                        onClick={() => {
                          setIsFilterOpen(false);
                          setSelectedJobRole(tempJobRole);
                          setSelectedStatus(tempStatus);
                          setSelectedOwner(tempOwner);
                          setDateRange(tempDateRange);
                          setScoreRange(tempScoreRange);
                          setCurrentPage(1);
                          localStorage.setItem(
                            "candidateReportsJobRole",
                            tempJobRole
                          );
                          localStorage.setItem(
                            "candidateReportsStatus",
                            tempStatus
                          );
                          localStorage.setItem(
                            "candidateReportsOwner",
                            tempOwner
                          );
                          localStorage.setItem(
                            "candidateReportsDateRange",
                            JSON.stringify(tempDateRange)
                          );
                          localStorage.setItem(
                            "candidateReportsScoreRange",
                            JSON.stringify(tempScoreRange)
                          );
                          localStorage.setItem(
                            "candidateReportsCurrentPage",
                            1
                          );
                          const params = {
                            page: 1,
                            limit: itemsPerPage,
                            job_position:
                              tempJobRole === "Job Role" ? "All" : tempJobRole,
                            combined_status:
                              tempStatus === "Status" ? "All" : tempStatus,
                            // Owner filtering is done client-side
                            start_date: formatDate(tempDateRange[0]),
                            end_date: formatDate(tempDateRange[1], true),
                            search_query: searchQuery || null,
                            min_score: tempScoreRange[0],
                            max_score: tempScoreRange[1],
                          };
                          fetchData(params, tempOwner);
                          topRef.current?.scrollIntoView({
                            behavior: "smooth",
                          });
                        }}
                        className="flex-1 px-4 py-2 bg-gradient-to-r from-pink-500 to-blue-500 text-white rounded-full text-sm hover:opacity-90"
                      >
                        Apply Now
                      </button>
                    </div>
                  </div>
                )}
              </div>
              <div className="relative" ref={columnFilterRef}>
                <button
                  className="flex items-center space-x-4 dark:glass-effect bg-bgPrimary border border-gray-600 rounded-full px-4 py-2 dark:text-gray-300 text-textSecondary dark:hover:bg-gray-700/50 hover:bg-gray-50 text-sm font-light"
                  onClick={() => setIsColumnFilterOpen(!isColumnFilterOpen)}
                >
                  <Layout className="w-4 h-4" />
                  <span>Columns</span>
                  {(!visibleColumns.candidateName ||
                    !visibleColumns.jobRole ||
                    visibleColumns.invitedDate ||
                    visibleColumns.scheduledBy ||
                    !visibleColumns.interviewScore ||
                    !visibleColumns.status) && (
                    <XCircleIcon
                      className="w-5 h-5"
                      onClick={(e) => {
                        e.stopPropagation();
                        nonFixedColumnReset();
                      }}
                    />
                  )}
                </button>
                {isColumnFilterOpen && (
                  <div
                    className="absolute right-0 mt-2 w-60 border border-gray-600 rounded-lg shadow-lg z-20 p-6"
                    style={{
                      background:`${theme === 'dark' ? 'linear-gradient(233.57deg, rgba(34, 52, 97, 0.25) -5.01%, rgba(11, 10, 31, 0.05) 100%)' : ""}`,
                      opacity: 1,
                      backdropFilter: "blur(500px)",
                    }}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-medium dark:text-[#D9D9D9] text-textPrimary">
                        Columns
                      </h3>
                      <button
                        onClick={() => setIsColumnFilterOpen(false)}
                        className="dark:text-gray-400 text-textSecondary dark:hover:text-white hover:text-gray-400"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                    <div className="flex flex-col gap-2">
                      <div className="flex space-x-3 mb-4">
                        <button
                          onClick={handleSelectAllColumns}
                          className="flex-1 px-2 py-1 bg-gradient-to-r from-pink-500 to-blue-500 text-white rounded-full text-xs hover:opacity-90"
                        >
                          Select All
                        </button>
                        {/* <button
                          onClick={handleUnselectAllColumns}
                          className="flex-1 px-2 py-1 border border-gray-600 rounded-full text-gray-300 hover:bg-gray-700/50 text-xs"
                        >
                          Unselect All
                        </button> */}
                      </div>
                      {/* <label className="flex items-center text-sm dark:text-[#D8D8D8] text-textSecondary">
                        <input
                          type="checkbox"
                          checked={visibleColumns.candidateName}
                          onChange={() => handleColumnToggle("candidateName")}
                          className="mr-2 accent-blue-500"
                        />
                        Candidate Name
                      </label> */}
                      {/* <label className="flex items-center text-sm dark:text-[#D8D8D8] text-textSecondary">
                        <input
                          type="checkbox"
                          checked={visibleColumns.jobRole}
                          onChange={() => handleColumnToggle("jobRole")}
                          className="mr-2 accent-blue-500"
                        />
                        Job Role
                      </label> */}
                      <label className="flex items-center text-sm dark:text-[#D8D8D8] text-textSecondary">
                        <input
                          type="checkbox"
                          checked={visibleColumns.invitedDate}
                          onChange={() => handleColumnToggle("invitedDate")}
                          className="mr-2 accent-blue-500"
                        />
                        Invited Date
                      </label>
                      <label className="flex items-center text-sm dark:text-[#D8D8D8] text-textSecondary">
                        <input
                          type="checkbox"
                          checked={visibleColumns.scheduledBy}
                          onChange={() => handleColumnToggle("scheduledBy")}
                          className="mr-2 accent-blue-500"
                        />
                        Owner
                      </label>
                      {/* <label className="flex items-center text-sm dark:text-[#D8D8D8] text-textSecondary">
                        <input
                          type="checkbox"
                          checked={visibleColumns.interviewScore}
                          onChange={() => handleColumnToggle("interviewScore")}
                          className="mr-2 accent-blue-500"
                        />
                        Interview Score
                      </label> */}
                       {/* <label className="flex items-center text-sm dark:text-[#D8D8D8] text-textSecondary">
                        <input
                          type="checkbox"
                          checked={visibleColumns.contactNumber}
                          onChange={() => handleColumnToggle("contactNumber")}
                          className="mr-2 accent-blue-500"
                        />
                        Contact Number
                      </label> */}
                      {/* <label className="flex items-center text-sm dark:text-[#D8D8D8] text-textSecondary">
                        <input
                          type="checkbox"
                          checked={visibleColumns.email}
                          onChange={() => handleColumnToggle("email")}
                          className="mr-2 accent-blue-500"
                        />
                        Email
                      </label> */}
                      {/* <label className="flex items-center text-sm dark:text-[#D8D8D8] text-textSecondary">
                        <input
                          type="checkbox"
                          checked={visibleColumns.status}
                          onChange={() => handleColumnToggle("status")}
                          className="mr-2 accent-blue-500"
                        />
                        Status
                      </label> */}
                    </div>
                  </div>
                )}
              </div>
              <div className="relative group flex items-center">
                <button
                  className="flex items-center space-x-4 dark:glass-effect bg-bgPrimary border border-gray-600 rounded-full px-4 py-2 dark:text-gray-300 text-textSecondary dark:hover:bg-gray-700/50 hover:bg-gray-50 text-sm font-light"
                  onClick={handleDownloadExcel}
                >
                  <Download className="w-4 h-4" />
                  <span>Download Excel</span>
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          {areCandidatesLoading ? (
            <div className="flex items-center justify-center min-h-[400px]">
              <Loader />
            </div>
          ) : sortedCandidates.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              No candidates found.
            </div>
          ) : (
            <div className="mx-4">
              <table className="w-full  border-[#5A4479D4] table-fixed">
                <thead>
                  <tr className="border-b border-[#5A4479D4]">
                    {visibleColumns.candidateName && (
                      <th
                        className="text-left text-xs dark:text-[#D8D8D8] text-textPrimary font-medium py-4 px-12 w-48 relative cursor-pointer"
                        onClick={() => handleSort("name")}
                      >
                        <div className="flex items-center gap-6">
                          Candidate Name
                          {sortColumn === "name" ? (
                            sortDirection === "asc" ? (
                              <ArrowUpAZ className="w-4 h-4 mr-2 cursor-pointer dark:hover:text-white hover:text-gray-300" />
                            ) : (
                              <ArrowDownZA className="w-4 h-4 mr-2 cursor-pointer dark:hover:text-white hover:text-gray-300" />
                            )
                          ) : (
                            <ArrowUpDown className="w-4 h-4 mr-2 cursor-pointer dark:hover:text-white hover:text-gray-300" />
                          )}
                        </div>
                      </th>  
                    )}
                    {visibleColumns.jobRole && (
                      <th className="text-left text-xs dark:text-[#D8D8D8] text-textPrimary font-medium py-4 w-40">
                        Job Role
                      </th>
                    )}
                    {visibleColumns.contactNumber && (
                      <th className="text-left text-xs dark:text-[#D8D8D8] text-textPrimary font-medium py-4 w-32">
                        Contact Number
                      </th>
                    )}  
                    {visibleColumns.email && (
                      <th className="text-left text-xs dark:text-[#D8D8D8] text-textPrimary font-medium py-4 w-64">
                        Email
                      </th>
                    )}  
                    {visibleColumns.invitedDate && (
                      <th className="text-left text-xs dark:text-[#D8D8D8] text-textPrimary font-medium py-4 w-32">
                        Invited Date
                      </th>
                    )}
                    {visibleColumns.scheduledBy && (
                      <th className="text-left text-xs dark:text-[#D8D8D8] text-textPrimary font-medium py-4 w-40">
                        Owner
                      </th>
                    )}
                    {visibleColumns.interviewScore && (
                      <th
                        className="text-left text-xs dark:text-[#D8D8D8] text-textPrimary] font-medium py-4 w-32 relative cursor-pointer"
                        onClick={() => handleSort("interview_score")}
                      >
                        <div className="flex items-center gap-5">
                          Interview Score
                          {sortColumn === "interview_score" ? (
                            sortDirection === "asc" ? (
                              <ArrowUp10 className="w-4 h-4 ml-1 cursor-pointer dark:hover:text-white hover:text-gray-400" />
                            ) : (
                              <ArrowDown10 className="w-4 h-4 ml-1 cursor-pointer dark:hover:text-white hover:text-gray-400" />
                            )
                          ) : (
                            <ArrowDown10 className="w-4 h-4 ml-1 opacity-50 dark:hover:text-white hover:text-gray-400" />
                          )}
                        </div>
                      </th>

                        
                    )}
                    {visibleColumns.status && (
                      <th className="text-left text-xs dark:text-[#D8D8D8] text-textPrimary font-medium py-4 w-24">
                        Status
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {sortedCandidates.map((candidate, index) => (
                    <tr
                      key={index}
                      className="cursor-pointer border-b dark:border-gray-700/30 border-gray-300/30 dark:hover:bg-gray-700/20 hover:bg-gray-100 transition-colors"
                      onClick={() => onViewCandidate(candidate?.interview_id)}
                    >
                      {visibleColumns.candidateName && (
                        <td className="py-4 px-12  dark:text-[#CCCCCCE5] text-textSecondary text-sm w-48 truncate">
                          {candidate?.candidate_name || "N/A"}
                        </td>
                      )}
                      {visibleColumns.jobRole && (
                        <td className="py-4   dark:text-[#CCCCCCE5] text-textSecondary text-sm w-48 truncate">
                          <div 
                            className="truncate" 
                            title={candidate.jdposition || "N/A"}  // ← This creates the tooltip
                          >
                            {candidate.jdposition || "N/A"}
                          </div>
                        </td>
                      )}
                      {visibleColumns.contactNumber && (
                        <td className="py-4 dark:text-[#CCCCCCE5] text-textSecondary text-sm w-32 truncate">
                          {candidate.contact_number || "N/A"}
                        </td>
                      )}

                      {visibleColumns.email && (
                        <td className="py-4 dark:text-[#CCCCCCE5] text-textSecondary text-sm w-64 truncate">
                          <div 
                            className="truncate" 
                            title={candidate.candidate_email || "N/A"}  // ← This creates the tooltip
                          >
                            {candidate.candidate_email || "N/A"}
                          </div>
                        </td>
                      )}

                      {visibleColumns.invitedDate && (
                        <td className="py-4  dark:text-[#CCCCCCE5] text-textSecondary text-sm w-48 truncate">
                          {candidate.invited_date || "N/A"}
                        </td>
                      )}
                      {visibleColumns.scheduledBy && (
                        <td className="py-4  dark:text-[#CCCCCCE5] text-textSecondary text-sm w-48 truncate">
                          {candidate.scheduled_by_name || "N/A"}
                        </td>
                      )}
                      {visibleColumns.interviewScore && (
                        <td
                        
                          className={`py-4 text-sm w-32 ${getScoreColor(
                            candidate.interview_percent
                          )}`}
                        >
                          {isNaN(candidate.interview_percent) ||
                          candidate.interview_percent === "NA"
                            ? "NA"
                            : Number(candidate.interview_percent).toFixed(2)}
                        </td>
                      )}
                      {visibleColumns.status && (
                        <td
                          className={`py-4 font-medium text-sm w-24 ${getStatusColor(
                            candidate.combined_status
                          )}`}
                        >
                          {candidate.combined_status || "N/A"}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="flex items-center justify-start">
                <span className="text-gray-400 text-sm m-10">
                  Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
                  {Math.min(currentPage * itemsPerPage, totalCandidates)} of{" "}
                  {totalCandidates} Candidates
                </span>
                <div className="flex items-center space-x-2">
                  <button
                    className="p-2 rounded-full border border-gray-600 text-gray-400 dark:hover:bg-gray-700/50 hover:bg-gray-200 disabled:opacity-50"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    <ChevronsLeft className="w-4 h-4 border border-[#D9D9D9] rounded-md" />
                  </button>
                  {getPageRange().map((page) => (
                    <button
                      key={page}
                      className={`w-8 h-8 rounded-full text-sm font-medium ${
                        page === currentPage
                          ? "text-[#D9D9D9] bg-gradient-to-r from-[#FF60B6] to-[#2960FD]"
                          : "border border-gray-600 text-gray-400 dark:hover:bg-gray-700/50 hover:bg-gray-200"
                      }`}
                      onClick={() => handlePageChange(page)}
                    >
                      {page}
                    </button>
                  ))}
                  {totalPages > maxPageButtons &&
                    currentPage < totalPages - maxPageButtons + 1 && (
                      <span className="text-gray-400 px-2">...</span>
                    )}
                  {totalPages > maxPageButtons &&
                    currentPage < totalPages - maxPageButtons + 1 && (
                      <button
                        className="w-8 h-8 rounded-full border border-gray-600 text-gray-400  dark:hover:bg-gray-700/50 hover:bg-gray-200 text-sm"
                        onClick={() => handlePageChange(totalPages)}
                      >
                        {totalPages}
                      </button>
                    )}
                  <button
                    className="p-2 rounded-full border border-gray-600 text-gray-400 dark:hover:bg-gray-700/50 hover:bg-gray-200 disabled:opacity-50"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    <ChevronsRight className="w-4 h-4 border border-[#D9D9D9] rounded-md" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CandidateReports;
